'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';
import { Fragment, useMemo } from 'react';

export function ListboxWithPlaceHolder<T extends { id?: any, code?: any, name?: string, label?: string }>({
                                                                                                       className,
                                                                                                       placeholder,
                                                                                                       autoFocus,
                                                                                                       'aria-label': ariaLabel,
                                                                                                       children: options,
                                                                                                       value,
                                                                                                       onChange,
                                                                                                       getDisplayValue,
                                                                                                       ...props
                                                                                                     }: {
  className?: string
  placeholder?: React.ReactNode
  autoFocus?: boolean
  'aria-label'?: string
  children?: React.ReactNode
  value?: T | null
  onChange?: (value: T | null) => void
  getDisplayValue?: (item: T) => string
} & Omit<Headless.ListboxProps<typeof Fragment, T>, 'as' | 'multiple' | 'value' | 'onChange'>) {
  // Extract options from children
  const optionsList = useMemo(() => {
    if (!options) return [];
    return React.Children.toArray(options).map((child: any) => child.props.value).filter(Boolean);
  }, [options]);

  // Generate display text for button
  const displayText = useMemo(() => {
    if (!value) return placeholder;
    return getDisplayValue ? getDisplayValue(value) : (value.label || value.name);
  }, [value, placeholder, getDisplayValue]);

  return (
    <Headless.Listbox value={value} onChange={onChange} multiple={false}>
      <Headless.ListboxButton
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        className={clsx([
          className,
          // Basic layout
          'group relative block w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'dark:before:hidden',
          // Hide default focus styles
          'focus:outline-none',
          // Focus ring
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:data-[focus]:ring-2 after:data-[focus]:ring-secondary-600',
          // Disabled state
          'data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none',
        ])}
      >
                <span
                  className={clsx([
                    // Basic layout
                    'relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
                    // Set minimum height for when no value is selected
                    'min-h-11 sm:min-h-9',
                    // Horizontal padding
                    'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]',
                    // Typography
                    'text-left text-base/6 placeholder:text-zinc-500 dark:text-white sm:text-sm/6 forced-colors:text-[CanvasText]',
                    // Border
                    'border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20',
                    // Background color
                    'bg-transparent dark:bg-white/5',
                    // Invalid state
                    'group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600',
                    // Disabled state
                    'group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15',
                    // Placeholder styling
                   !value ? 'text-zinc-500' : 'text-zinc-950 dark:text-white',
                    'truncate'
                  ])}
                >
                    {displayText}
                </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg
                      className="size-5 stroke-zinc-500 group-data-[disabled]:stroke-zinc-600 dark:stroke-zinc-400 sm:size-4 forced-colors:stroke-[CanvasText]"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      fill="none"
                    >
                        <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
      </Headless.ListboxButton>

      <Headless.ListboxOptions
        transition
        anchor="selection start"
        className={clsx(
          // Anchor positioning
          '[--anchor-offset:-1.625rem] [--anchor-padding:theme(spacing.4)] sm:[--anchor-offset:-1.375rem]',
          // Base styles
          'isolate w-max min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1',
          // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
          'outline outline-1 outline-transparent focus:outline-none',
          // Handle scrolling when menu won't fit in viewport
          'overflow-y-scroll overscroll-contain',
          // Popover background
          'bg-white backdrop-blur-xl dark:bg-zinc-800/75',
          // Shadows
          'shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10',
          // Transitions
          'transition-opacity duration-100 ease-in data-[transition]:pointer-events-none data-[closed]:data-[leave]:opacity-0',
          // Z-index
          'z-30',
          // Set max width and height
          'max-h-80 max-w-xs'
        )}
      >

        {/* Options */}
        <div className="max-h-64 overflow-y-auto">
          {optionsList.map((option: T, index: number) => {
              const key = option.id ?? option.code ?? (option as any).value ?? index;
              const isSelected = value && (
                value === option
                //(value.id ?? value.code ?? (value as any).value) === (option.id ?? option.code ?? (option as any).value)
              );
              const displayValue = getDisplayValue ? getDisplayValue(option) : (option.label || option.name || '');

              return (
                <Headless.ListboxOption
                  key={key}
                  value={option}
                  className={({ active }) => clsx(
                    'relative cursor-pointer select-none py-2 pl-8 pr-4 text-sm',
                    active ? 'bg-primary-950/5 text-primary-950 dark:bg-white/10 dark:text-white' : 'text-zinc-900 dark:text-zinc-100'
                  )}
                >
                  <span className={clsx('block truncate', isSelected ? 'font-medium' : 'font-normal')}>
                      {displayValue}
                  </span>
                  <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                                        {isSelected && (
                                          <svg className="size-4 text-secondary-600 dark:text-secondary-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                          </svg>
                                        )}
                                    </span>
                </Headless.ListboxOption>
              );
            }
          )}
        </div>
      </Headless.ListboxOptions>
    </Headless.Listbox>
  );
}
